import React, { useState } from 'react';
import { useMutation } from 'react-query';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  Divider,
} from '@mui/material';
import { Search as SearchIcon, History as HistoryIcon, ArrowForward } from '@mui/icons-material';
import { format } from 'date-fns';
import { voucherService } from '../services/api';
import { Voucher, StatusHistoryResponse } from '../types/voucher';

const VoucherInquiry: React.FC = () => {
  const [voucherNumber, setVoucherNumber] = useState('');
  const [voucher, setVoucher] = useState<Voucher | null>(null);
  const [statusHistory, setStatusHistory] = useState<StatusHistoryResponse[]>([]);

  const searchMutation = useMutation(
    (voucherNum: string) => voucherService.getVoucher(voucherNum),
    {
      onSuccess: (data) => {
        setVoucher(data);
        // Fetch status history after successfully getting voucher
        console.log('Fetching status history for voucher:', data.voucherNumber);
        voucherService.getVoucherStatusHistory(data.voucherNumber)
          .then(history => {
            console.log('Status history received:', history);
            setStatusHistory(history);
          })
          .catch(error => {
            console.error('Error fetching status history:', error);
            console.error('Error details:', error.response);
          });
      },
      onError: () => {
        setVoucher(null);
        setStatusHistory([]);
      },
    }
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (voucherNumber.trim()) {
      searchMutation.mutate(voucherNumber.trim());
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'AWAITING_PAYMENT':
        return 'warning';
      case 'CUSTOMER_PAID':
        return 'success';
      case 'AWAITING_RETAILER_SETTLEMENT':
        return 'info';
      case 'AWAITING_MERCHANT_SETTLEMENT':
        return 'info';
      case 'SETTLED':
        return 'success';
      case 'EXPIRED':
        return 'error';
      case 'REFUNDED':
        return 'default';
      case 'CANCELLED':
        return 'error';
      default:
        return 'primary';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ');
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Voucher Inquiry
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSearch}>
          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              label="Voucher Number"
              placeholder="Enter voucher number (e.g., ZETDC-7B3D9A1C)"
              value={voucherNumber}
              onChange={(e) => setVoucherNumber(e.target.value)}
              disabled={searchMutation.isLoading}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={searchMutation.isLoading || !voucherNumber.trim()}
              startIcon={searchMutation.isLoading ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              {searchMutation.isLoading ? 'Searching...' : 'Search'}
            </Button>
          </Box>
        </form>
      </Paper>

      {searchMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Voucher not found. Please check the voucher number and try again.
        </Alert>
      )}

      {voucher && (
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h5" component="div">
                    Voucher Details
                  </Typography>
                  <Chip
                    label={getStatusLabel(voucher.voucherStatus)}
                    color={getStatusColor(voucher.voucherStatus) as any}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Voucher Number
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {voucher.voucherNumber}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Merchant ID
                </Typography>
                <Typography variant="body1">
                  {voucher.merchantId}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Order Reference
                </Typography>
                <Typography variant="body1">
                  {voucher.merchantOrderReference}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Amount
                </Typography>
                <Typography variant="h6" color="primary">
                  {voucher.currency} {voucher.amount.toFixed(2)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  Product Description
                </Typography>
                <Typography variant="body1">
                  {voucher.productDescription}
                </Typography>
              </Grid>

              {voucher.dateCreated && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(voucher.dateCreated), 'PPpp')}
                  </Typography>
                </Grid>
              )}

              {voucher.expiresAt && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Expires At
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(voucher.expiresAt), 'PPpp')}
                  </Typography>
                </Grid>
              )}

              {voucher.paidAt && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Paid At
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(voucher.paidAt), 'PPpp')}
                  </Typography>
                </Grid>
              )}

              {voucher.retailerId && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Retailer ID
                  </Typography>
                  <Typography variant="body1">
                    {voucher.retailerId}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {voucher && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <HistoryIcon sx={{ mr: 1 }} />
              <Typography variant="h5" component="div">
                Status History
              </Typography>
            </Box>
            {statusHistory.length === 0 ? (
              <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                No status history available for this voucher.
              </Typography>
            ) : (
            <List>
              {statusHistory.map((history, index) => (
                <React.Fragment key={history.id}>
                  <ListItem alignItems="flex-start" sx={{ flexDirection: 'column', py: 2 }}>
                    <Box display="flex" justifyContent="space-between" width="100%" mb={1}>
                      <Typography variant="caption" color="textSecondary">
                        {format(new Date(history.transitionDate), 'PPp')}
                        {history.timeSinceTransition && ` (${history.timeSinceTransition})`}
                      </Typography>
                      {history.isAutomatic && (
                        <Chip label="Automatic" size="small" variant="outlined" />
                      )}
                    </Box>

                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      {history.fromStatus && (
                        <>
                          <Chip
                            label={getStatusLabel(history.fromStatus)}
                            size="small"
                            color={getStatusColor(history.fromStatus) as any}
                          />
                          <ArrowForward fontSize="small" />
                        </>
                      )}
                      <Chip
                        label={getStatusLabel(history.toStatus)}
                        size="small"
                        color={getStatusColor(history.toStatus) as any}
                      />
                    </Box>

                    {history.changeReason && (
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                        <strong>Reason:</strong> {history.changeReason}
                      </Typography>
                    )}

                    {history.comments && (
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                        <strong>Comments:</strong> {history.comments}
                      </Typography>
                    )}

                    {history.changedBy && (
                      <Typography variant="caption" color="textSecondary">
                        Changed by: {history.changedBy}
                      </Typography>
                    )}

                    {history.triggerEvent && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        Trigger: {history.triggerEvent}
                      </Typography>
                    )}
                  </ListItem>
                  {index < statusHistory.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default VoucherInquiry;